<script setup>
import { computed } from 'vue';
import draggable from 'vuedraggable';
import PagePicker from './PagePicker.vue';
import { toMenuItem } from './menu';

// A role's sidebar menu as it will appear: ordered pages and groups (e.g.
// Utilities) with their sub-pages. Drag handles reorder, x removes, and the
// pickers only offer pages the menu doesn't contain yet.
const menu = defineModel({ type: Array, required: true });
const props = defineProps({
  pages: { type: Array, default: () => [] }, // the page catalog
});

const usedIds = computed(() => {
  const ids = new Set();
  for (const item of menu.value) {
    ids.add(item.id);
    for (const sub of item.subLink || []) ids.add(sub.id);
  }
  return ids;
});
const availableTop = computed(() => props.pages.filter((p) => !usedIds.value.has(p.id)));
const availableSub = computed(() =>
  props.pages.filter((p) => !usedIds.value.has(p.id) && Number(p.isParent) !== 1),
);

function addTop(id) {
  const page = props.pages.find((p) => p.id === id);
  if (page) menu.value = [...menu.value, toMenuItem(page)];
}
function addSub(group, id) {
  const page = props.pages.find((p) => p.id === id);
  if (page) group.subLink.push(toMenuItem(page));
}
function removeTop(item) {
  menu.value = menu.value.filter((i) => i !== item);
}
function removeSub(group, sub) {
  group.subLink = group.subLink.filter((s) => s !== sub);
}
</script>

<template>
  <div class="menu">
    <draggable
      v-model="menu"
      item-key="id"
      handle=".menu__handle"
      :animation="150"
      tag="ul"
      class="menu__list"
    >
      <template #item="{ element: item }">
        <li class="menu__item">
          <div class="menu__row">
            <v-icon class="menu__handle" size="18" aria-label="Drag to reorder">mdi-drag-vertical</v-icon>
            <v-icon size="18" class="menu__icon">{{ item.icon || 'mdi-file-outline' }}</v-icon>
            <span class="menu__name">{{ item.title }}</span>
            <span v-if="item.isParent" class="menu__meta">Group · {{ item.subLink.length }}</span>
            <span v-else class="menu__meta menu__meta--route">{{ item.route }}</span>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="x-small"
              class="menu__remove"
              :aria-label="`Remove ${item.title}`"
              @click="removeTop(item)"
            />
          </div>

          <div v-if="item.isParent" class="menu__group">
            <draggable
              v-model="item.subLink"
              item-key="id"
              handle=".menu__handle"
              :animation="150"
              :group="`sub-${item.id}`"
              tag="ul"
              class="menu__sublist"
            >
              <template #item="{ element: sub }">
                <li class="menu__row menu__row--sub">
                  <v-icon class="menu__handle" size="18" aria-label="Drag to reorder">mdi-drag-vertical</v-icon>
                  <span class="menu__name">{{ sub.title }}</span>
                  <span class="menu__meta menu__meta--route">{{ sub.route }}</span>
                  <v-btn
                    icon="mdi-close"
                    variant="text"
                    size="x-small"
                    class="menu__remove"
                    :aria-label="`Remove ${sub.title}`"
                    @click="removeSub(item, sub)"
                  />
                </li>
              </template>
            </draggable>
            <div class="menu__add-sub">
              <PagePicker
                :pages="availableSub"
                :label="`Add to ${item.title}`"
                :placeholder="`Search pages to add to ${item.title}…`"
                empty-text="Every page is already in this menu."
                size="small"
                @pick="(id) => addSub(item, id)"
              />
            </div>
          </div>
        </li>
      </template>
    </draggable>

    <p v-if="!menu.length" class="menu__empty">
      This role's sidebar is empty. Add the first page below.
    </p>

    <div>
      <PagePicker
        :pages="availableTop"
        label="Add page or group"
        placeholder="Search pages and groups…"
        @pick="addTop"
      />
    </div>
  </div>
</template>

<style scoped>
.menu {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.menu__list,
.menu__sublist {
  margin: 0;
  padding: 0;
  list-style: none;
}
.menu__list {
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--card);
}
.menu__list:empty {
  display: none;
}
.menu__item + .menu__item {
  border-top: 1px solid var(--border);
}
.menu__row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 4px 6px 4px 4px;
  font-size: 14px;
}
.menu__row--sub {
  min-height: 38px;
}
.menu__handle {
  color: var(--faint-foreground);
  cursor: grab;
}
.menu__handle:active {
  cursor: grabbing;
}
.menu__icon {
  color: var(--secondary-foreground);
}
.menu__name {
  font-weight: 500;
  white-space: nowrap;
}
.menu__meta {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: var(--muted-foreground);
}
.menu__meta--route {
  color: var(--faint-foreground);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.menu__remove {
  opacity: 0.6;
}
.menu__remove:hover {
  opacity: 1;
}
.menu__group {
  margin: 0 12px 10px 34px;
  padding-left: 12px;
  border-left: 1px solid var(--border);
}
.menu__add-sub {
  padding: 6px 0 2px;
}
.menu__empty {
  margin: 0;
  padding: 20px;
  border: 1px dashed var(--border-strong);
  border-radius: 10px;
  color: var(--muted-foreground);
  font-size: 14px;
  text-align: center;
}
</style>
